import {useMutation, useQuery} from "@tanstack/react-query";
import {useClient} from "../../../utils/hooks";
import {PaginationMeta} from "../../../redux/service/i-service";
import {AxiosError} from "axios";
import {
  CustomerGetCanEditReviewApiResponse,
  CustomerGetReviewApiResponse,
  GetReviewsApiResponse,
  ReviewOfferingType,
  SingleReviewsApiResponse,
} from "../types";
import {
  COACH_REVIEWS_QUERY_KEY,
  CUSTOMER_CAN_EDIT_REVIEW_QUERY_KEY,
  CUSTOMER_REVIEW_QUERY_KEY,
} from "../constants";
import {AnalyticsEvent} from "../../../utils/analytics";
import {message} from "antd";

type V2ReviewItem = {
  id: string;
  userId: number;
  offeringId: number;
  offeringType: string;
  offeringName?: string;
  emailAddress: string;
  firstName: string;
  lastName: string;
  review: string;
  rating: number;
  createdAt: string;
  updatedAt: string;
};

type V2ReviewsResponse = {
  data: V2ReviewItem[];
  total: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
};

const mapV2ReviewToSingleReview = (
  v2Review: V2ReviewItem,
  total: number
): SingleReviewsApiResponse => ({
  id: v2Review.id,
  user_id: v2Review.userId,
  offering_id: v2Review.offeringId,
  offering_type: v2Review.offeringType as ReviewOfferingType,
  email_address: v2Review.emailAddress,
  first_name: v2Review.firstName,
  last_name: v2Review.lastName,
  review: v2Review.review,
  rating: v2Review.rating,
  createdAt: v2Review.createdAt,
  updatedAt: v2Review.updatedAt,
  offering_name: v2Review.offeringName ?? "",
  total_count: String(total),
});

const mapV2PaginationToMeta = (
  v2Data: V2ReviewsResponse,
  page: number,
  per_page: number
): PaginationMeta => ({
  current_page: page,
  next_page: v2Data.hasNextPage ? page + 1 : 0,
  previous_page: v2Data.hasPrevPage ? page - 1 : 0,
  per_page: per_page,
  total_pages: Math.ceil(v2Data.total / per_page),
});

export const useGetReviews = (page: number = 1, per_page: number = 5) => {
  const client = useClient("REVIEWS_AND_RATINGS");

  return useQuery<
    {data: GetReviewsApiResponse; meta: PaginationMeta},
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [COACH_REVIEWS_QUERY_KEY, {page}],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        perPage: String(per_page),
      });
      return client(`reviews?${params}`).then((res) => {
        const v2Data: V2ReviewsResponse = res.data;
        return {
          data: {
            ratingsAndReviews: v2Data.data.map((item) =>
              mapV2ReviewToSingleReview(item, v2Data.total)
            ),
          },
          meta: mapV2PaginationToMeta(v2Data, page, per_page),
        };
      });
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });
};

const mapV2CustomerReviewToResponse = (v2Data: {
  id: string;
  userId: number;
  offeringId: number;
  offeringType: string;
  emailAddress: string;
  firstName: string;
  lastName: string;
  review: string;
  rating: number;
  createdAt: string;
  updatedAt: string;
}): CustomerGetReviewApiResponse => ({
  id: v2Data.id,
  user_id: v2Data.userId,
  offering_id: v2Data.offeringId,
  offering_type: v2Data.offeringType as ReviewOfferingType,
  email_address: v2Data.emailAddress,
  first_name: v2Data.firstName,
  last_name: v2Data.lastName,
  review: v2Data.review,
  rating: v2Data.rating,
  createdAt: v2Data.createdAt,
  updatedAt: v2Data.updatedAt,
});

export const useGetCustomerReview = ({
  emailAddress,
  offering_id,
  offering_type,
}: {
  offering_id: number;
  offering_type: ReviewOfferingType;
  emailAddress: string;
}) => {
  const client = useClient("REVIEWS_AND_RATINGS");
  return useQuery<
    CustomerGetReviewApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [CUSTOMER_REVIEW_QUERY_KEY, {emailAddress, offering_id, offering_type}],
    queryFn: () => {
      const params = new URLSearchParams({
        emailAddress,
        offeringId: String(offering_id),
        offeringType: offering_type,
      });
      return client(`public/reviews/customer/fetch?${params}`, {
        token: undefined,
      }).then((res) => mapV2CustomerReviewToResponse(res.data));
    },
    refetchInterval: 300000,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: !!emailAddress && !!offering_id,
  });
};
export const useGetCustomerCanEditReview = ({
  emailAddress,
  offering_id,
  offering_type,
}: {
  offering_id: number;
  offering_type: ReviewOfferingType;
  emailAddress: string;
}) => {
  const client = useClient("REVIEWS_AND_RATINGS");
  return useQuery<
    CustomerGetCanEditReviewApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [
      CUSTOMER_CAN_EDIT_REVIEW_QUERY_KEY,
      {emailAddress, offering_id, offering_type},
    ],
    queryFn: () => {
      const params = new URLSearchParams({
        emailAddress,
        offeringType: offering_type,
        offeringId: String(offering_id),
      });
      return client(`public/reviews/check?${params}`, {token: undefined}).then(
        (data) => data
      );
    },
    refetchInterval: 300000,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: !!emailAddress,
  });
};

export const useReviewsExportCSV = () => {
  const client = useClient("REVIEWS_AND_RATINGS");
  return useMutation({
    mutationFn: () =>
      client("reviews/export", {
        method: "GET",
        responseType: "blob" as const,
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
      AnalyticsEvent("Coach reviews", "Coach reviews export CSV fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Coach reviews", "Coach reviews export CSV success");
    },
  });
};
